import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { logger } from '@/lib/utils/logger';
import { getCurrentOrganizationId } from '@/entities/user';
import { resetToolAccess, setToolAccess } from '../../api/tools-access.api';
import type { WorkspaceAccess } from '../../model/tools.types';

interface SaveToolAccessParams {
  agentId: string;
  newAccess: WorkspaceAccess;
  newWorkspaces: WorkspaceResponseDto[];
}

export function useSaveToolAccess() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: SaveToolAccessParams) => {
      const organizationId = getCurrentOrganizationId();

      if (params.newAccess === 'all') {
        await resetToolAccess(params.agentId, organizationId);
      } else {
        const workspaceIds = params.newWorkspaces.map((ws) => ws.id);
        await setToolAccess(params.agentId, organizationId, params.newAccess, workspaceIds);
      }
    },
    onSuccess: async () => {
      // String literals rather than ADMIN_QUERY_KEYS: the factory has no root
      // key covering all of `['admin', 'tools', ...]`, and the two consumer
      // caches belong to other slices.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin', 'tools'] }),
        queryClient.invalidateQueries({ queryKey: ['agent-visibility'] }),
        queryClient.invalidateQueries({ queryKey: ['springboard-tools'] }),
      ]);
    },
    onError: (error) => {
      logger.error('Failed to save tool access:', error);
    },
  });

  return {
    // Rejects on failure. Callers close their dialog once this resolves, so a
    // resolved failure would tell the admin a change landed that did not.
    // `isError` carries the same failure as state, for callers that render it.
    save: mutation.mutateAsync,
    isSaving: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}
